import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useQueryClient } from 'react-query';

import type { ApiClient, IApiClientAuthorization } from '~/lib/api';
import type { IAppRegion } from '~/lib/appSettings';
import type {
  IAuthAsUser,
  IAuthClientOptions,
  IAuthCredentials,
  IAuthLogOutOptions,
  IAuthNavigateToLogInOptions,
} from './AuthClient';
import type { IAuthContextValue } from './AuthContext';
import { ApiError } from '~/lib/api';
import { AuthClient } from './AuthClient';
import { AuthContext } from './AuthContext';
import { initialAuthState } from './AuthState';
import { MultiSessionsContext } from './MultiSessionsContext';
import { reducer } from './reducer';

/**
 * The main configuration to instantiate the `AuthProvider`.
 */
export type IAuthProviderOptions = IAuthClientOptions & {
  /**
   * The child nodes your Provider has wrapped.
   */
  children?: React.ReactNode;

  /**
   * Context to be used when creating the AuthProvider, defaults to the
   * internally created context.
   */
  context?: React.Context<IAuthContextValue>;

  clientId: string;

  apiClient: ApiClient;
};

/**
 * Provides the AuthContext to its child components.
 */
export const AuthProvider: React.FC<IAuthProviderOptions> = (props) => {
  const {
    children,
    context = AuthContext,
    clientId,
    apiClient,
    ...clientOptions
  } = props;
  const { current: client } = useRef(
    new AuthClient(clientId, apiClient, clientOptions),
  );
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const didInitialiseRef = useRef(false);
  const queryClient = useQueryClient();
  const multiSessionsContext = useContext(MultiSessionsContext);

  useEffect(() => {
    if (didInitialiseRef.current) {
      return;
    }
    didInitialiseRef.current = true;

    const sessionId = multiSessionsContext.currentId;
    if (!sessionId) {
      dispatch({ type: 'LOGIN_ERROR' });
      return;
    }

    void client.restoreFromCache(sessionId).then((entry) => {
      if (!entry) {
        dispatch({ type: 'LOGIN_ERROR' });
        return;
      }

      void client
        .getUser(sessionId)
        .then((user) => dispatch({ type: 'INITIALIZED', user }));

      return apiClient.auth.authorize().catch((error) => {
        if (error instanceof ApiError) {
          const isForbidden = error.errors.some((e) => e.code === 'forbidden');
          if (isForbidden) {
            void client.clearCredentials(sessionId);
            dispatch({ type: 'LOGIN_ERROR' });
            return;
          }
        }

        throw error;
      });
    });
  }, [client, apiClient, multiSessionsContext]);

  const navigateToLogIn = useCallback(
    (options?: IAuthNavigateToLogInOptions): Promise<void> =>
      client.getAllCredentials().then((credentials) =>
        client.navigateToLogIn({
          ...options,
          hasMultiSessions: credentials.length > 1,
        }),
      ),
    [client],
  );

  const logInWithCredentials = useCallback(
    (
      region: IAppRegion,
      credentials: IAuthCredentials,
    ): Promise<IApiClientAuthorization> => {
      if (!state.isAuthenticated) {
        dispatch({ type: 'LOGIN_STARTED' });
        queryClient.clear();
      }

      return client
        .logInWithCredentials(region, credentials)
        .then((credentials) => {
          multiSessionsContext.add({
            sessionId: credentials.sessionId,
            isCurrent: true,
          });

          return credentials;
        })
        .catch((error) => {
          if (!state.isAuthenticated) {
            dispatch({
              type: 'LOGIN_ERROR',
              error: error instanceof Error ? error : undefined,
            });
          }

          throw error;
        });
    },
    [client, multiSessionsContext, queryClient, state],
  );

  const logInAs = useCallback(
    (
      region: IAppRegion,
      asUser: IAuthAsUser,
    ): Promise<IApiClientAuthorization> =>
      client
        .logInAs(region, asUser)
        .then((credentials) => {
          multiSessionsContext.add({
            sessionId: credentials.sessionId,
          });

          return credentials;
        })
        .catch((error) => {
          throw error;
        }),
    [client, multiSessionsContext],
  );

  const setAuthorization = useCallback(
    async (authorization: IApiClientAuthorization) => {
      await client.setAuthorization(authorization);

      dispatch({
        type: 'LOGIN_COMPLETE',
        user: authorization.user,
      });
    },
    [client],
  );

  const clearCredentials = useCallback(
    async (sessionId?: string) => {
      await client.clearCredentials(sessionId);
      dispatch({ type: 'LOGOUT_COMPLETE' });
    },
    [client],
  );

  const logOut = useCallback(
    async (options: IAuthLogOutOptions = {}): Promise<void> => {
      const currentSessionId = multiSessionsContext.currentId;
      const sessionIds =
        options.sessionIds ??
        (await client.getAllCredentials()).map(({ sessionId }) => sessionId);
      const willCloseCurrentSession =
        currentSessionId !== undefined && sessionIds.includes(currentSessionId);

      if (willCloseCurrentSession) {
        dispatch({ type: 'LOGOUT_STARTED' });

        return client
          .logOut({
            ...options,
            sessionIds,
          })
          .then(() => dispatch({ type: 'LOGOUT_COMPLETE' }))
          .catch((error) => {
            dispatch({
              type: 'LOGOUT_ERROR',
              error: error instanceof Error ? error : undefined,
            });

            throw error;
          })
          .finally(() => {
            queryClient.clear();
          });
      } else {
        return client.logOut(options);
      }
    },
    [client, queryClient, multiSessionsContext],
  );

  const getClient = useCallback(() => client, [client]);

  const switchSession = useCallback(
    (sessionId: string) =>
      client.restoreFromCache(sessionId).then((authorization) => {
        if (!authorization) {
          dispatch({ type: 'LOGIN_ERROR' });

          return;
        }

        dispatch({
          type: 'LOGIN_COMPLETE',
          user: authorization.user,
        });

        return authorization;
      }),
    [client],
  );

  const contextValue: IAuthContextValue = {
    ...state,
    navigateToLogIn,
    logInWithCredentials,
    logInAs,
    logOut,
    setAuthorization,
    clearCredentials,
    getClient,
    switchSession,
  };

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
